//#region Import

import store from "@/store";
import swal from "sweetalert";

import AirwayPanel from "./AirwayPanel.vue";
import AirwayDicomPanel from "./AirwayDicomPanel.vue";
import AirwayButtons from "./AirwayButtons.vue";
import AirwayHeatmap from "./AirwayHeatmap.vue";
import AirwaySelectedSlice from "./AirwaySelectedSlice.vue";
import AirwayTopControls from "./AirwayTopControls.vue";

import { faceMesh, faceItem } from "@/components/controls/TeethListManager.js";
import {
  drawBox,
  drawLine,
  drawTubeLine,
  drawRectangle,
  drawTubeRectangle,
  loadFont,
  drawText,
  getTextGeometry,
} from "../draw/draw";

import { mutationTypes } from "@/store/modules/dicom";
import { getFiguresForPlane } from "./AirwayColor.js";
import { DynamicTubeGeometry } from "@/components/geometry/DynamicTubeGeometry";
import {
  initDwvCommons,
  loadDicomUrls,
  loadDicomZip,
  updateAppLayout,
  updateOriginBox,
  focusDicomsTo3dPoint,
  DicomBox,
  updateSliceIntersection,
  setSliceIndex,
  showLoaderPopup,
  get3dSlicePoints,
} from "@/components/dicom/DicomManager.js";
import { drawOutlines, destroyOutlines } from "@/components/dicom/slice";
import { addSceneLights } from "@/components/anchorage/AnchorageMode";
import { getMimeType } from "@/components/reports/ReportsManager.js";
import { getFormattedDate, saveFile } from "@/components/reports/fileMethods";
import { getMouse3dDirection } from "../geometry/editObject";

import { calculateDistance2D, calculateDistance3D } from "../geometry/geometry";

//#endregion

//#region Fields
// const slice
const ANIMATE_INTERVAL_MS = 300;
const empty = Object.freeze([]);
const axialPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
const lineMeshMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
const videoLineMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
const textMaterial = new THREE.MeshBasicMaterial({
  color: 0xffffff,
  transparent: true,
  side: 2,
  depthTest: false,
  depthWrite: false,
});

const rotateAxisY = new THREE.Vector3(0, 1, 0);
const rotateAxisZ = new THREE.Vector3(0, 0, 1);

const defLineRadius = 0.2;
const ringThickness = 0.5;
let sliceOffset = 0.6;

let controlsInitialized = false;
let airwaysJsonObj = null;
let airwaySliceMeshes = null;

let airway = null;
let airwayMesh = null;
let sliceMesh = null;
let globalLineMesh = null;
let ellipsesMesh = null;
let currentSlicePosition = null;

let airwaysData = {
  isSquareOutline: true,
  isLineView: true,
  isCutSlice: false,
  isAverageEllipses: false,
  total_volume: null,
  slices: [
    {
      name: "Minimal Area Slice",
      filename: "slice_min_area.png",
      imageUrl: "",
      jsonKey: "minimal_area_slice",
      selected: false,
      area: 0,
      ap_diameter: 0,
      rl_diameter: 0,
      slice_position: 0,
      position_text: null,
      ellipsesUrl: false,
    },
    {
      name: "Minimal AP Slice",
      filename: "slice_min_ap.png",
      imageUrl: "",
      jsonKey: "minimal_ap_diam_slice",
      selected: false,
      area: 0,
      ap_diameter: 0,
      rl_diameter: 0,
      slice_position: 0,
      position_text: null,
      ellipsesUrl: false,
    },
    {
      name: "Minimal Right-Left Slice",
      filename: "slice_min_rl.png",
      imageUrl: "",
      jsonKey: "minimal_rl_diam_slice",
      selected: false,
      area: 0,
      ap_diameter: 0,
      rl_diameter: 0,
      slice_position: 0,
      position_text: null,
      ellipsesUrl: false,
    },
    {
      name: "Top Slice",
      filename: "slice_top.png",
      imageUrl: "",
      jsonKey: "top_slice",
      selected: false,
      area: 0,
      ap_diameter: 0,
      rl_diameter: 0,
      slice_position: sliceOffset,
      position_text: "0.6",
      ellipsesUrl: false,
    },
    {
      name: "Bottom Slice",
      filename: "slice_bottom.png",
      imageUrl: "",
      jsonKey: "bottom_slice",
      selected: false,
      area: 0,
      ap_diameter: 0,
      rl_diameter: 0,
      slice_position: -sliceOffset,
      position_text: null,
      ellipsesUrl: false,
    },
  ],
  has_selected: false,
  selected_slice: {
    area: 0,
    ap_diameter: 0,
    rl_diameter: 0,
    slice_position: 0,
    position_text: null,
  },
  ellipsesData: null,
  isEllipsesDataEnabled: false,
  ellipsesInfo1: null,
  ellipsesInfo2: null,
};
let areaRel = null;
let apRel = null;
let rlRel = null;
let slicesLength = airwaysData.slices.length;
let bottomSlice = airwaysData.slices[slicesLength - 1];

let dwv = require("dwv");
let _airwayDicomInitialized = false;
let _airwayAppArr = [new dwv.App(), new dwv.App(), new dwv.App()];
let _orientations = ["axial", "sagittal", "coronal"];
let _colors = ["red", "blue", "green"];
let _vFroms = [
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(0, 0, 1),
];
let _dicomBox;
let _originBox = null;
let _dicomLoaded = false;

let clickPoint = new THREE.Vector3();
let clickControlPoint = new THREE.Vector3();
let offset = new THREE.Vector3();
let movedControlMesh = null;
let updateTime = null;

let videoScene = null;
let videoRecorder = null;
let videoExtension = null;

let topSlicePosition = null;
let bottomSlicePosition = null;
let lineMeshes = {};

//#endregion

//#region Init
let AirwayMode = {
  isActive: false,
  isWhite: true,
  vcenter: null,
  dicomIncompatible: true,
  nonOcclusalable: true,
  whiteColor: "#ffffff",
  blueColor: "#007bff",
  modeTag: "airway",
  modeCursor: "ns-resize",
  enterMode: function () {
    enterAirwayMode();
  },
  leaveMode: function (saveActiveMode) {
    leaveAirwayMode(false, saveActiveMode);
  },
  mouseUp: function (event) {
    if (movedControlMesh) {
      movedControlMesh = null;
      viewer.controls.enabled = true;
      return true;
    }
    return false;
  },
  mouseMove: function (event) {
    if (movedControlMesh) {
      let deltaTime = window.performance.now() - updateTime;
      if (deltaTime <= 20) return;
      updateTime = window.performance.now();
      if (event.preventDefault) event.preventDefault();

      let direction = getMouse3dDirection(event);
      if (!direction) return;
      raycaster.set(viewer.camera.position, direction);

      let intersects = raycaster.intersectObject(implantPlane);
      if (intersects.length > 0) {
        let point = intersects[0].point.sub(offset);
        let newY = point.y;
        let airwayBox = airway.defaultBox;
        if (newY <= airwayBox.max.y && newY >= airwayBox.min.y) {
          movedControlMesh.position.setY(newY);
          focusDicomToSlicePosition(newY);
        }
      }
    }
    return true;
  },
  mouseClick: function (event) {
    return false;
  },
  tagDetected: function (tag, mesh, isShift, intersectPoint) {
    console.log("tagDetected AirwayMode", mesh, isShift, intersectPoint);
    viewer.controls.enabled = false;
    updateTime = window.performance.now();

    movedControlMesh = mesh;
    let center = movedControlMesh.position;
    implantPlane.position.copy(center);
    implantPlane.lookAt(viewer.camera.position);
    clickControlPoint.copy(intersectPoint);
    let intersects = raycaster.intersectObject(implantPlane);
    clickPoint.copy(intersects[0].point);
    offset.copy(intersects[0].point).sub(implantPlane.position);
  },
};

async function enterAirwayMode() {
  console.log("enterAirwayMode", new Date());
  AirwayMode.isWhite = vueApp.isWhite;
  AirwayMode.whiteColor = vueApp.whiteColor;
  AirwayMode.blueColor = vueApp.blueColor;

  if (vueApp.isDicomOpen) {
    store.commit(mutationTypes.isAxialChange, false);
    store.commit(mutationTypes.isSaggitalChange, false);
    store.commit(mutationTypes.isCoronalChange, false);
    $(".dicom-container").css({ display: "none" });
    updateAppLayout();
  }

  if (vueApp.isSplitView) {
    vueApp.switchSplitView();
  }
  if (vueApp.isDicom3DView) {
    vueApp.switchDicom3dView();
  }

  airway = vueApp.airways && vueApp.airways.length ? vueApp.airways[0] : null;
  if (!airway) return;
  airwayMesh = viewer.get_model_mesh(airway.id);

  if (!airwaysJsonObj) {
    airwaysJsonObj = await loadAirwaysJson();
  }

  updateLineMeshes();

  initializeControls();
  vueApp.leaveModes(false, true);

  $(".teeth-list-ctrl").css("display", "none");
  $(".cbct-zoom-container").css("display", "none");
  $(".airway-mode-item").css("display", "block");

  if (faceMesh && faceItem) {
    faceMesh.visible = false;
  }
  vueApp.allItems.forEach((model) => {
    if (model != airway) {
      let mesh = viewer.get_model_mesh(model.id);
      if (mesh && mesh.geometry) mesh.visible = false;
      model.isHidden = true;
    }
  });

  airwayMesh.visible = false;
  if (!airway.defaultBox) {
    let airwayGeometry = airwayMesh.baseGeometry
      ? airwayMesh.baseGeometry
      : airwayMesh.geometry;
    if (!airwayGeometry.boundingBox) {
      airwayGeometry.computeBoundingBox();
    }
    airway.defaultBox == offsetBox(airwayGeometry.boundingBox);
  }

  let airwayBox = airway.defaultBox;
  console.log("airwayBox", airwayBox);

  if (!bottomSlice.position_text) {
    bottomSlice.position_text = (airwayBox.size.y - 0.6).toFixed(2);
  }

  vueApp.switchToDefaultView();
  let totalCount = vueApp.allItems.length;
  if (totalCount == 1 && vueApp.airways.length == 1) {
    $("#airwayExitBtn").css({ display: "none" });
  } else {
    viewer.camera.position.z = defaultZoom / 2 + airwayBox.center.z;
  }
  AirwayMode.defaultTargetPosition = new THREE.Vector3(
    airwayBox.center.x,
    airwayBox.center.y,
    airwayBox.center.z,
  );
  vueApp.lookAtPoint(AirwayMode.defaultTargetPosition);
  AirwayMode.defaultCameraPosition = viewer.camera.position.clone();

  let selectedSlice = airwaysData.slices.find((s) => s.selected);
  if (!selectedSlice) selectedSlice = airwaysData.slices[0];
  selectSlice(selectedSlice);
  airwayMesh.visible = true;

  vueApp.activeMode = AirwayMode;
  AirwayMode.isActive = true;

  await loadAirwayDicomApps();
}

function initializeControls() {
  if (!controlsInitialized) {
    airwaysData.slices.forEach((slice) => {
      slice.imageUrl = `${vueApp.stlBaseUrl}/${slice.filename}`;
    });

    console.log("airwaysData", airwaysData);

    var ctrls = [
      AirwayHeatmap,
      AirwaySelectedSlice,
      AirwayTopControls,
      AirwayPanel,
      AirwayDicomPanel,
      AirwayButtons,
    ];
    ctrls.forEach((component) => {
      const componentInstance = new Vue({
        ...component,
        propsData: { mode: AirwayMode, airwaysData: airwaysData },
      });
      const vueContainer = document.createElement("div");
      document.getElementById("stl_wrapper").appendChild(vueContainer);
      componentInstance.$mount(vueContainer);
    });
    controlsInitialized = true;
  }
}

async function loadAirwaysJson() {
  let airwaysJsonUrl = `${vueApp.stlBaseUrl}/airways_volume_analysis.json`;
  try {
    airwaySliceMeshes = {};
    airwaysJsonObj = {};

    let response = await fetch(airwaysJsonUrl);
    airwaysJsonObj = await response.json();

    airwaysData.total_volume =
      (airwaysJsonObj.total_volume / 1000).toFixed(3) + " cc";
    console.log("airwaysJsonObj", airwaysJsonObj);
    airwaysData.slices.forEach((slice) => {
      if (slice.jsonKey && airwaysJsonObj[slice.jsonKey]) {
        slice.area = airwaysJsonObj[slice.jsonKey].area.toFixed(2);
        slice.ap_diameter =
          airwaysJsonObj[slice.jsonKey].ap_diameter.toFixed(2);
        slice.rl_diameter =
          airwaysJsonObj[slice.jsonKey].rl_diameter.toFixed(2);
        slice.slice_position = airwaysJsonObj[slice.jsonKey].slice_position;
        slice.position_text =
          airwaysJsonObj[slice.jsonKey].slice_position.toFixed(2);
      }
    });

    try {
      let ellipsesJson = `${vueApp.stlBaseUrl}/output_airways_lambda/measurements_summary.json`;
      let ellipsesResponse = await fetch(ellipsesJson);
      let ellipsesJsonObj = await ellipsesResponse.json();
      console.log("ellipsesJsonObj", ellipsesJsonObj);
      if (ellipsesJsonObj) {
        airwaysData.ellipsesData = ellipsesJsonObj;
        airwaysData.slices[0].ellipsesUrl = `${vueApp.stlBaseUrl}/output_airways_lambda/airways_ellipses.png`;
        airwaysData.isEllipsesDataEnabled = true;
        let avgArea = +ellipsesJsonObj.regression_ellipse.area.toFixed(2);
        let minArea =
          +ellipsesJsonObj.measurements.minimal_area_slice.area.toFixed(1);
        let percent = ((avgArea * 100) / minArea).toFixed(2);
        airwaysData.ellipsesInfo1 = `Avg area for this size skull is ${avgArea} mm`;

        let percentDif = +percent - 100;
        let percentDifRound = percentDif.toFixed(2);

        airwaysData.ellipsesInfo2 =
          percentDif > 0
            ? `. It is bigger by ${percentDifRound}% of the current patient minimal area.`
            : `. It is smaller by ${percentDifRound}% of the current patient minimal area.`;

        let img = new Image();
        img.src = airwaysData.slices[0].ellipsesUrl;
      }
    } catch (error) {
      console.log(`ellipsesJsonObj error: ${error}`);
    }
  } catch (error) {
    console.log(`loadAirwaysJson error: ${error}`);
    airwaysJsonObj = {};
  }

  return airwaysJsonObj;
}

//#endregion

//#region Dicom

async function loadAirwayDicomApps() {
  if (!_airwayDicomInitialized) {
    _airwayDicomInitialized = true;
    try {
      if (!vueApp.dicomLowLink) await loadDicomUrls();

      let dicomZipData = await loadDicomZip(vueApp.dicomLowLink, false);
      console.log(
        "loadAirwayDicomApps",
        dicomZipData ? dicomZipData.byteLength : 0,
      );
      if (dicomZipData && dicomZipData.byteLength) {
        initDwvCommons();
        _airwayAppArr.forEach((app, index) => {
          app.isMobileDevice = vueApp.isMobileDevice; //v-if="!isMobileDevice"
          app.index = index;
          app.isNoTeeth = true;
          app.isReadonly = true;
          app.viewIndex = app.index !== 0 ? app.index - 1 : 2;
          app.color = _colors[index];
          app.vFrom = _vFroms[index];
          app.orientation = _orientations[index];
          app.divId = `${app.orientation}AirwayDiv`;
          app.spinnerId = `${app.orientation}AirwaySpinner`;
          app.toolName = "dragDrop";
          app.opacity = 0.5;
          app.sliceOpacity = 0.25;
          app.outlineThickness = 1;
          app.annotationColor = "#CC5500";
          app.segmentationType = "fillAll";
          app.segmentations = ["airway"];
          app.showSegmentation = true;
          app.showIntersection = true;
          app.show3DLayer = true;
          app.isLoading = false;
          app.isAnnotating = false;
          app.isEditingAnnotation = false;
          app.show3DFiller = true;
          app.annotationFillSegmentations = true;
          app.annotationOpacity = 0.5;
          app.text1 = app.text2 = app.text3 = "";
          app.visible = true;
          app.parentApp = 1;

          const viewConfig = new dwv.ViewConfig(app.divId);
          viewConfig.orientation = app.orientation;

          const viewConfigs = { "*": [viewConfig] };
          const options = new dwv.AppOptions(viewConfigs);
          options.viewOnFirstLoadItem = false;
          app.init(options);
          app.tools = options.tools;

          app.addEventListener("load", (event) => {
            console.log(`airway app ${app.index} load`, event);
            $id(app.spinnerId).style.display = "none";
            app.render(event.loadid);
          });
          app.addEventListener("loadstart", (event) => {
            console.log(`airway app ${app.index} loadstart`, event);
          });
          app.addEventListener("renderend", () => {
            if (!app.isRendered) {
              console.log(`airway app ${app.index} renderend`, event);
              const layerGroup = app.getLayerGroupByDivId(app.divId);
              const activeLayer = layerGroup.getActiveViewLayer();

              app.baseSize = activeLayer.getBaseSize();
              const viewController = activeLayer.getViewController();
              app.worldSize = viewController.getImageWorldSize();
              app.spacing = viewController.get2DSpacing();
              app.geometry = viewController.getViewImage().getGeometry();
              // app.getImage(0).getGeometry();
              app.size = app.geometry.getSize().getValues();
              app.spacing = app.geometry.getSpacing().getValues();
              var origin = app.geometry.getOrigin();
              app.origin = [+origin.getX(), +origin.getY(), +origin.getZ()];

              let image = app.getLastImage();
              if (!image) image = viewController.getViewImage();
              if (image) {
                const size = image.getGeometry().getSize();
                app.max = size.get(app.viewIndex);
                app.current = Math.floor(app.max / 2) + 1;
              }
              updateOriginBox(app, viewController);
              app.isRendered = true;
              let renderedCount = _airwayAppArr.filter(
                (x) => x.isRendered,
              ).length;
              if (renderedCount == _airwayAppArr.length) {
                // afterDicomRendering();
              }
            }
          });
          app.addEventListener("loadabort", (event) => {
            console.log(`airway app ${app.index} loadabort`, event);
          });
          app.addEventListener("loaderror", (event) => {
            console.log(`airway app ${app.index} loaderror`, event);
          });
        });

        let app0 = _airwayAppArr[0];
        app0.addEventListener("loadend", (event) => {
          viewer.controls.keysEnabled = false;
          console.log(`airway app 0 loadend`, event);
          const image0 = app0.getLastImage(); //getImage(0);
          if (!image0) {
            const layerGroup = app0.getLayerGroupByDivId(app0.divId);
            const activeLayer = layerGroup.getActiveViewLayer();
            const viewController = activeLayer.getViewController();
            image0 = viewController.getViewImage();
          }
          app0.isOrcaLoaded = true;
          for (let index = 1; index < _airwayAppArr.length; index++) {
            const app = _airwayAppArr[index];
            app.addEventListener("loadend", (event) => {
              console.log(`airway app ${app.index} loadend`, event);
              app.isOrcaLoaded = true;
              let loadedCount = _airwayAppArr.filter(
                (x) => x.isOrcaLoaded,
              ).length;
              if (loadedCount == _airwayAppArr.length) {
                $id("loader").style.display = "none";

                setTimeout(function () {
                  _dicomLoaded = true;
                  window.addEventListener("resize", airwayWindowResized);
                  focusDicomToSlicePosition();

                  document.getElementById("airwayVideoBtn").disabled = false;
                  document.getElementById("airwayVideoBtn").style.cursor =
                    "pointer";
                }, 300);
              }
            });
            app.addNewImage(image0.clone());
          }
        });

        app0.addEventListener("positionchange", () => {
          const layerGroup = app0.getLayerGroupByDivId(app0.divId);
          const viewLayer = layerGroup.getActiveViewLayer();
          const viewController = viewLayer.getViewController();
          let newIndex = viewController.getCurrentIndex().get(app0.viewIndex);
          let newCurrent = newIndex + 1;
          if (app0.current != newCurrent && DicomBox) {
            let newSlicePosition = DicomBox.min.y;
            newSlicePosition += DicomBox.size.y * (newIndex / (app0.max - 1));
            if (sliceMesh) sliceMesh.position.setY(newSlicePosition);
            app0.current = newCurrent;
            focusDicomToSlicePosition(newSlicePosition, true);

            // if (airwaysData.isCutSlice) {
            //   updateCutSlice();
            // }
            // app0.current = newCurrent;
            // const geometry = app0.getLastImage().getGeometry();
            // const position = geometry.indexToWorld(currentIndex);
            // const p2D = viewController.getPlanePositionFromPosition(position);
            // const displayPos = viewLayer.planePosToDisplay(p2D.x, p2D.y);
            // console.log("displayPos", displayPos, currentSlicePosition);
            // drawOutlines(app0, DicomBox);
            // updateSliceIntersection(_airwayAppArr[1], _airwayAppArr, true);
            // updateSliceIntersection(_airwayAppArr[2], _airwayAppArr, true);
          }
        });

        $id("loader").style.display = "none";

        _airwayAppArr[0].loadImageObject([
          { name: "dicom", filename: "dicom.zip", data: dicomZipData },
        ]);
      }
    } catch (e) {
      console.log(`loadAirwayDicomApps error: ${e}`);
    }
  }
}

function airwayWindowResized() {
  if (_dicomLoaded) {
    console.log("airwayWindowResized");
    for (const app of _airwayAppArr) {
      app.onResize();
      app.resetZoom();
      let currentConfigs = app.getDataViewConfigs();
      app.setDataViewConfigs(currentConfigs);
      for (let i = 0; i < app.getNumberOfLoadedData(); ++i) {
        app.render(i);
      }
      if (app.index > 0) updateSliceIntersection(app, _airwayAppArr, true);
    }
  }
}

function focusDicomToSlicePosition(slicePosition, fromPositionChange) {
  let airwayBox = airway.defaultBox;
  let center = airwayBox.center;
  let fromSelectedSlice = !slicePosition;
  if (fromSelectedSlice) {
    let slice = airwaysData.selected_slice;
    slicePosition =
      slice.slice_position > 0
        ? airwayBox.max.y - slice.slice_position
        : airwayBox.min.y - slice.slice_position;
  }

  slicePosition = +slicePosition.toFixed(2);
  center = new THREE.Vector3(
    center.x,
    slicePosition,
    airwayBox.min.z + 0.2 * airwayBox.size.z,
  );
  currentSlicePosition = slicePosition;

  if (!fromSelectedSlice) {
    if (globalLineMesh) viewer.scene.remove(globalLineMesh);
    globalLineMesh = lineMeshes[slicePosition];
    if (!globalLineMesh) {
      globalLineMesh = lineMeshes[slicePosition] =
        getLineMeshForSlicePosition(slicePosition);
    }
    if (globalLineMesh) {
      if (airwaysData.isLineView) {
        viewer.scene.add(globalLineMesh);
      }
      airwaysData.selected_slice = {
        area: globalLineMesh.area,
        ap_diameter: globalLineMesh.ap_diameter,
        rl_diameter: globalLineMesh.rl_diameter,
        slice_position: globalLineMesh.slicePosition,
        position_text: globalLineMesh.slicePosition.toFixed(2),
      };
    } else {
      airwaysData.selected_slice = {};
    }
  }

  let anyChange =
    fromPositionChange || focusDicomsTo3dPoint(center, _airwayAppArr);
  if (anyChange) {
    for (const app of _airwayAppArr) {
      if (fromSelectedSlice || app.index == 0) {
        if (fromSelectedSlice) {
          console.log("fromSelectedSlice");
          setSliceIndex(app, app.current - 1);
        }
        drawOutlines(app, DicomBox);
      }
      // drawOutlines(app, DicomBox);

      if (app.index > 0) {
        updateSliceIntersection(app, _airwayAppArr, true);
      }
    }
  }

  if (airwaysData.isCutSlice) {
    updateCutSlice();
  }
}

//#endregion

//#region Video

function generateAirwayVideo() {
  console.log("generateAirwayVideo");

  vueApp.airwayMessage = "Generating airway video...";

  $id("airwayVideoImg").style.display = "inline-block";

  swal(
    "Generating airway video started. You can continue to use the other application functions.",
  );

  if (!videoScene) {
    videoScene = createVideoScene();
    let videoCanvas = videoScene.renderer.domElement;
    videoCanvas.style.position = "absolute";
    videoCanvas.style.right = "5px";
    videoCanvas.style.top = "40px";
    videoCanvas.style.zIndex = "999";

    // let container = document.getElementById("views_cont");
    // container.appendChild(videoCanvas);

    let mType = getMimeType();
    let RecordRTC = require("recordrtc");
    videoRecorder = RecordRTC(videoCanvas, {
      type: "canvas",
      mimeType: mType,
      disableLogs: true,
      frameInterval: 59,
      // recorderType: CanvasRecorder,
    });
    let mTypeArr = mType.split("/");
    videoExtension =
      mTypeArr.length && mTypeArr.length > 1 ? mTypeArr[1] : "webm";
  }

  let axialMesh = airwayMesh.axialMesh;
  let sagittalMesh = airwayMesh.sagittalMesh;
  let frameMesh = airwayMesh.frameMesh;
  let textMesh1 = airwayMesh.textMesh1;
  let textMesh2 = airwayMesh.textMesh2;
  let cloneMesh = airwayMesh.cloneMesh;
  let videoPlane = airwayMesh.videoPlane;

  let airwayBox = airway.defaultBox;
  let center = airwayBox.center;
  let min = airwayBox.min;
  let max = airwayBox.max;

  videoScene.camera.position.copy(AirwayMode.defaultCameraPosition.clone());
  videoScene.camera.lookAt(AirwayMode.defaultTargetPosition.clone());
  videoScene.camera.updateProjectionMatrix();

  if (!axialMesh) {
    axialMesh = airwayMesh.axialMesh = new THREE.Mesh(
      airwayMesh.geometry.clone(),
      airwayMesh.material.clone(),
    );
    videoScene.add(axialMesh);
    axialMesh.position.copy(airwayMesh.position);
    // axialMesh.rotateOnAxis(rotateAxisY, Math.PI / 2.5);
    // axialMesh.rotateOnAxis(rotateAxisZ, Math.PI / 3);
    axialMesh.rotateOnAxis(rotateAxisY, Math.PI / 2);
    axialMesh.rotateOnAxis(rotateAxisZ, Math.PI / 2);
    axialMesh.position.setY(airwayMesh.position.y + 12);
    axialMesh.position.setZ(airwayMesh.position.z + 20);

    axialMesh.updateMatrix();

    axialMesh.geometry.computeBoundingBox();

    let axialBox = axialMesh.geometry.boundingBox.clone();
    axialBox.applyMatrix4(axialMesh.matrix);
    axialMesh.box = axialBox = offsetBox(axialBox);
    axialMesh.defaultZ = axialMesh.position.z;

    let slicePosition = axialBox.max.z;
    videoPlane = airwayMesh.videoPlane = new THREE.Plane(
      new THREE.Vector3(0, 0, -1),
      slicePosition,
    );
    videoScene.renderer.clippingPlanes = [videoPlane];

    sagittalMesh = airwayMesh.sagittalMesh = new THREE.Mesh(
      airwayMesh.geometry.clone(),
      airwayMesh.material.clone(),
    );
    videoScene.add(sagittalMesh);
    sagittalMesh.rotateOnAxis(rotateAxisY, Math.PI / 3);
    sagittalMesh.geometry.applyMatrix4(sagittalMesh.matrix);

    sagittalMesh.position.setX(airwayMesh.position.x + 50);
    sagittalMesh.position.setY(airwayMesh.position.y);
    sagittalMesh.position.setZ(airwayMesh.position.z);
    // sagittalMesh.lookAt(AirwayMode.defaultCameraPosition);
    // sagittalMesh.rotateOnAxis(rotateAxisZ, Math.PI / 2);
    sagittalMesh.updateMatrix();
    sagittalMesh.geometry.computeBoundingBox();
    let sbox = sagittalMesh.geometry.boundingBox.clone();
    sbox.applyMatrix4(sagittalMesh.matrix);
    sagittalMesh.box = sbox = offsetBox(sbox);

    sagittalMesh.material.opacity = axialMesh.material.opacity = 1;
    sagittalMesh.material.side = axialMesh.material.side = THREE.DoubleSide;

    let app1 = _airwayAppArr[1];
    let app2 = _airwayAppArr[2];

    const canvas = $(`#${app1.divId}-layer-0 canvas`)[0];
    let texture = new THREE.CanvasTexture(canvas);
    texture.repeat.x = -1;
    texture.repeat.y = -1;

    let sliceSize = 50;
    let sliceDif = sliceSize / 2;
    let difX = 50;
    let difY = 0;
    let sliceCenter = new THREE.Vector3(
      center.x - difX,
      center.y - difY,
      center.z,
    );
    let slicePoints = [
      new THREE.Vector3(
        sliceCenter.x - sliceDif,
        sliceCenter.y + sliceDif,
        sliceCenter.z,
      ),
      new THREE.Vector3(
        sliceCenter.x + sliceDif,
        sliceCenter.y + sliceDif,
        sliceCenter.z,
      ),
      new THREE.Vector3(
        sliceCenter.x + sliceDif,
        sliceCenter.y - sliceDif,
        sliceCenter.z,
      ),
      new THREE.Vector3(
        sliceCenter.x - sliceDif,
        sliceCenter.y - sliceDif,
        sliceCenter.z,
      ),
    ];
    console.log("slicePoints", slicePoints);
    // drawLine(slicePoints[0], slicePoints[1], 0x00ff00, videoScene);
    // drawLine(slicePoints[1], slicePoints[2], 0x00ff00, videoScene);
    // drawLine(slicePoints[2], slicePoints[3], 0x00ff00, videoScene);
    // drawLine(slicePoints[3], slicePoints[0], 0x00ff00, videoScene);
    frameMesh = airwayMesh.frameMesh = drawRectangle(
      slicePoints,
      app1.color,
      app2.vFrom, //app1.vFrom, null,
      false,
      false,
      texture, // texture false true
      1,
      videoScene,
    );
    // frameMesh.rotateOnAxis(rotateAxisY, Math.PI / 8);
    frameMesh.lookAt(AirwayMode.defaultCameraPosition);
    let sliceMesh = frameMesh.children[0];
    sliceMesh.geometry.computeBoundingBox();
    let frameBox = sliceMesh.geometry.boundingBox.clone();
    frameBox.applyMatrix4(frameMesh.matrix);
    frameBox = offsetBox(frameBox);

    let dicomBox = offsetBox(app1.dicomBox);
    let maxSizeXZ = Math.max(dicomBox.size.x, dicomBox.size.z);
    if (dicomBox.size.y < maxSizeXZ) {
      let dify = maxSizeXZ - dicomBox.size.y;
      dicomBox = {
        min: {
          x: dicomBox.min.x,
          y: dicomBox.min.y - dify / 2,
          z: frameBox.min.z,
        },
        max: {
          x: dicomBox.max.x,
          y: dicomBox.max.y + dify / 2,
          z: dicomBox.max.z,
        },
      };
      dicomBox = offsetBox(dicomBox);
    }

    let airwayBox = sagittalMesh.box;
    let startYRel = (airwayBox.min.y - dicomBox.min.y) / dicomBox.size.y;
    let endYRel = (airwayBox.max.y - dicomBox.min.y) / dicomBox.size.y;

    let newBox = {
      min: {
        x: frameBox.min.x,
        y: frameBox.min.y + startYRel * frameBox.size.y,
        z: frameBox.min.z,
      },
      max: {
        x: frameBox.max.x,
        y: frameBox.min.y + endYRel * frameBox.size.y,
        z: frameBox.max.z,
      },
    };
    newBox = offsetBox(newBox);
    frameMesh.box = newBox;
  }

  let videoLineMeshes = Object.values(lineMeshes).filter(
    (m) => m && m.forVideo,
  );
  console.log("videoLineMeshes", videoLineMeshes.length);

  let sbox = sagittalMesh.box;
  let fbox = frameMesh.box;
  // drawBox(fbox, 0x00ff00, videoScene);

  setTimeout(() => {
    if (!textMesh1) {
      let textPosition1 = new THREE.Vector3(center.x, min.y, max.z); //(center.x - 3, min.y + 2, max.z);
      let top = 0.93;
      let depth = 0.998;
      textPosition1
        .set(-1 + 2 * 0.5, 1 - 2 * top, depth)
        .unproject(videoScene.camera);
      // let text1 = `Area (mm^2) \nPosition (mm) \nAP distance (mm) \nRL distance (mm) \n`;
      // `- ${lineMesh.area}\n- ${lineMesh.slicePosition}\n- ${lineMesh.ap_diameter}\n- ${lineMesh.rl_diameter}\n`;
      let text1 = `Area (mm^2) - 000\nPosition (mm) - 000\nAP distance (mm) - 000\nRL distance (mm) - 000\n`;

      let textGeometry1 = getTextGeometry(text1, vueApp.droidMonoFont, 1.2); // 1.2 0.2
      textMesh1 = airwayMesh.textMesh1 = new THREE.Mesh(
        textGeometry1,
        textMaterial,
      );
      textMesh1.renderOrder = 999;
      textMesh1.position.set(textPosition1.x, textPosition1.y, textPosition1.z);
      textMesh1.lookAt(videoScene.camera.position);
      videoScene.add(textMesh1);
    }
    textMesh1.visible = false;
    let counter = 0;
    for (; counter <= 100; counter++) {
      let lineMesh = videoLineMeshes[counter];
      if (!lineMesh.cloneMesh) {
        lineMesh.cloneMesh = cloneGroup(
          lineMesh,
          videoLineMaterial,
          axialMesh.matrix,
        );
        lineMesh.cloneMesh.position.setZ(
          lineMesh.slicePosition - defLineRadius * 2,
        );
      }

      if (!lineMesh.textGeometry) {
        let text = `Area (mm^2) - ${lineMesh.area}\nPosition (mm) - ${lineMesh.slicePosition}\nAP distance (mm) - ${lineMesh.ap_diameter}\nRL distance (mm) - ${lineMesh.rl_diameter}\n`;
        lineMesh.textGeometry = getTextGeometry(
          text,
          vueApp.droidMonoFont,
          1.2,
        );
      }
    }

    counter = 0;
    console.log("videoRecorder startRecording", videoRecorder);

    videoRecorder.startRecording();

    let timerId = setTimeout(async function tick() {
      if (counter <= 100) {
        const startDate = new Date();
        let lineMesh = videoLineMeshes[counter];
        axialMesh.position.setZ(axialMesh.defaultZ + lineMesh.slicePosition);

        if (cloneMesh) videoScene.remove(cloneMesh);
        cloneMesh = airwayMesh.cloneMesh = lineMesh.cloneMesh;
        videoScene.add(cloneMesh);

        await delay(100);

        let difY = (sbox.size.y * counter) / 100;
        let p1 = new THREE.Vector3(
          sbox.min.x,
          sbox.max.y - difY,
          sbox.center.z,
        );
        let p2 = new THREE.Vector3(
          sbox.max.x,
          sbox.max.y - difY,
          sbox.center.z,
        );
        sagittalMesh.line = drawTubeLine(
          p1,
          p2,
          0xff0000,
          0.15,
          videoScene,
          true,
          sagittalMesh.line,
        );

        difY = (fbox.size.y * counter) / 100;
        p1 = new THREE.Vector3(fbox.min.x, fbox.max.y - difY, fbox.center.z);
        p2 = new THREE.Vector3(fbox.max.x, fbox.max.y - difY, fbox.center.z);
        frameMesh.line = drawTubeLine(
          p1,
          p2,
          0xff0000,
          0.15,
          videoScene,
          true,
          frameMesh.line,
        );

        textMesh1.geometry = lineMesh.textGeometry;
        textMesh1.visible = true;

        const endDate = new Date();
        const durationMs = endDate.getTime() - startDate.getTime();
        console.log(`counter: ${counter}`, durationMs);
        counter++;
        timerId = setTimeout(
          tick,
          durationMs >= ANIMATE_INTERVAL_MS
            ? 1
            : ANIMATE_INTERVAL_MS - durationMs,
        );
      } else {
        setTimeout(() => {
          clearInterval(timerId);
          videoRecorder.stopRecording(function (url) {
            let fileName = `airwayVideo_${getFormattedDate(
              true,
            )}.${videoExtension}`;
            saveFile(url, fileName);
            document.getElementById("airwayVideoImg").style.display = "none";
            vueApp.airwayMessage = null;
          });
        }, 1000);
      }
    }, ANIMATE_INTERVAL_MS);
  }, 200);
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function createVideoScene() {
  const isMobileOrTablet = mobileAndTabletCheck();
  let width = isMobileOrTablet ? 1280 : 1920;
  let height = isMobileOrTablet ? 720 : 1080;
  width = 1280;
  height = 720;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#000000"); //new THREE.Color("#000000"); viewer.scene.background;

  const renderer = new THREE.WebGLRenderer();
  renderer.setSize(width, height);
  scene.renderer = renderer;

  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1e4);
  scene.add(camera);
  scene.camera = camera;
  scene.camera.aspect = width / height;
  scene.camera.updateProjectionMatrix();

  addSceneLights(scene, camera);

  function animate() {
    requestAnimationFrame(animate);
    renderer.render(scene, camera);
  }
  animate();
  return scene;
}

function cloneGroup(oldGroup, material, matrix) {
  let newGroup = new THREE.Group();

  for (const mesh of oldGroup.children) {
    let geometry = mesh.geometry; //.clone();
    let cloneMesh = new THREE.Mesh(geometry, material);
    cloneMesh.position.copy(mesh.position);
    if (matrix) {
      cloneMesh.rotateOnAxis(rotateAxisY, Math.PI / 2);
      cloneMesh.rotateOnAxis(rotateAxisZ, Math.PI / 2);
      cloneMesh.position.setY(airwayMesh.position.y + 12);
      cloneMesh.position.setZ(airwayMesh.position.z + 20);
      // cloneMesh.applyMatrix4(matrix);
    }

    newGroup.add(cloneMesh);
  }

  return newGroup;
}

//#endregion

//#region Meshes

function updateLineMeshes() {
  let airwayMesh = viewer.get_model_mesh(airway.id);
  airwayMesh.baseGeometry.computeBoundingBox();
  let airwayBox = offsetBox(
    airwayMesh.baseGeometry.boundingBox,
    airwayMesh.position.x,
    airwayMesh.position.y,
    airwayMesh.position.z,
  );
  airway.defaultBox = airwayBox;

  let topSlicePosition = airwayBox.max.y - sliceOffset;
  let bottomSlicePosition = airwayBox.min.y + sliceOffset;

  let sliceDifY = (topSlicePosition - bottomSlicePosition) / 100;
  for (let i = 100; i >= 0; i--) {
    let slicePosition = +(bottomSlicePosition + sliceDifY * i).toFixed(2);
    let lineMesh = getLineMeshForSlicePosition(slicePosition);
    if (lineMesh) {
      lineMesh.forVideo = true;
      lineMeshes[slicePosition] = lineMesh;
    }
  }

  let areaPercentCount = 0;
  let areaPercentSum = 0;
  let apPercentCount = 0;
  let apPercentSum = 0;
  let rlPercentCount = 0;
  let rlPercentSum = 0;

  airwaysData.slices.forEach((slice) => {
    let slicePosition =
      slice.slice_position > 0
        ? airwayBox.max.y - slice.slice_position
        : airwayBox.min.y - slice.slice_position;
    slicePosition = +slicePosition.toFixed(2);
    let lineMesh = lineMeshes[slicePosition];
    if (!lineMesh) {
      let tempMesh = getLineMeshForSlicePosition(slicePosition);
      if (tempMesh) lineMesh = lineMeshes[slicePosition] = tempMesh;
    }
    if (lineMesh) {
      airwaySliceMeshes[slice.filename] = lineMesh;
      if (slice.area > 0) {
        areaPercentCount++;
        let percent = slice.area / lineMesh.area - 1;
        areaPercentSum += percent;
      }
      if (slice.ap_diameter > 0) {
        apPercentCount++;
        let percent = slice.ap_diameter / lineMesh.ap_diameter - 1;
        apPercentSum += percent;
      }
      if (slice.rl_diameter > 0) {
        rlPercentCount++;
        let percent = slice.rl_diameter / lineMesh.rl_diameter - 1;
        rlPercentSum += percent;
      }
    }
  });
  apRel = 1 + apPercentSum / apPercentCount;
  rlRel = 1 + rlPercentSum / rlPercentCount;
  areaRel = 1 + areaPercentSum / areaPercentCount;

  for (const key in lineMeshes) {
    if (Object.hasOwnProperty.call(lineMeshes, key)) {
      const lineMesh = lineMeshes[key];
      lineMesh.ap_diameter *= apRel;
      lineMesh.rl_diameter *= rlRel;
      lineMesh.area *= areaRel;
      lineMesh.ap_diameter = +lineMesh.ap_diameter.toFixed(2);
      lineMesh.rl_diameter = +lineMesh.rl_diameter.toFixed(2);
      lineMesh.area = +lineMesh.area.toFixed(2);
    }
  }

  try {
    createEllipsesMesh();
  } catch (error) {
    console.log(`createEllipsesMesh error: ${error}`);
  }
}

function getLineMeshForSlicePosition(slicePosition) {
  axialPlane.set(new THREE.Vector3(0, -1, 0), slicePosition);
  let figures = getFiguresForPlane(airwayMesh, airwayMesh.geometry, axialPlane);
  let baseFigures =
    airwayMesh.geometry == airwayMesh.baseGeometry
      ? figures
      : getFiguresForPlane(airwayMesh, airwayMesh.baseGeometry, axialPlane);

  let lineMesh = getLineMeshFromFigures(figures, slicePosition);
  if (lineMesh) {
    lineMesh.figures = baseFigures;
    let firstMesh = lineMesh.children[0];
    let firstGeometry = firstMesh.geometry;
    let firstPosition = firstMesh.position;
    firstGeometry.computeBoundingBox();
    lineMesh.defaultBox = offsetBox(
      firstGeometry.boundingBox,
      firstPosition.x,
      firstPosition.y,
      firstPosition.z,
    );

    let airwayBox = airway.defaultBox;
    let area = 0;
    let ap_diameter = 0;
    let rl_diameter = 0;
    lineMesh.figures.forEach((figure, index) => {
      let figureArea = Math.abs(THREE.ShapeUtils.area(figure.vertices));
      area += figureArea;
      for (const vertice of figure.vertices) {
        let xVertices = figure.vertices.filter(
          (v) => Math.abs(v.x - vertice.x) < 0.1,
        );
        let yVertices = figure.vertices.filter(
          (v) => Math.abs(v.y - vertice.y) < 0.1,
        );
        for (const xvertice of xVertices) {
          let distance = calculateDistance2D(vertice, xvertice);
          if (distance > ap_diameter) {
            ap_diameter = distance;
          }
        }
        for (const yvertice of yVertices) {
          let distance = calculateDistance2D(vertice, yvertice);
          if (distance > rl_diameter) {
            rl_diameter = distance;
          }
        }
      }
      //figure.vertices
    });

    if (apRel) ap_diameter *= apRel;
    if (rlRel) rl_diameter *= rlRel;
    if (areaRel) area *= areaRel;

    lineMesh.ap_diameter = +ap_diameter.toFixed(2);
    lineMesh.rl_diameter = +rl_diameter.toFixed(2);
    lineMesh.area = +area.toFixed(2);
    lineMesh.slicePosition = +(airwayBox.max.y - slicePosition).toFixed(2);
  }
  return lineMesh;
}

async function createEllipsesMesh() {
  if (airwaysData.ellipsesData) {
    let ellipsesData = airwaysData.ellipsesData;
    let ellipse_center =
      ellipsesData.ellipse_centers.ellipse_center.world_coordinates;

    let slice = airwaysData.slices[0];
    let airwayBox = airway.defaultBox;
    let slicePosition =
      slice.slice_position > 0
        ? airwayBox.max.y - slice.slice_position
        : airwayBox.min.y - slice.slice_position;
    ellipse_center.z = ellipse_center.y;
    ellipse_center.y = slicePosition;

    let lineMesh = airwaySliceMeshes[slice.filename];
    ellipse_center.x = lineMesh.defaultBox.center.x;
    ellipse_center.z = lineMesh.defaultBox.center.z;

    const group = new THREE.Group();

    const extrudeSettings = {
      depth: ringThickness,
      // amount: 2,
      bevelEnabled: false,
      // bevelSegments: 30,
      // steps: 30,
      // bevelSize: 0.3,
      // bevelThickness: 0.5,
      curveSegments: 100,
    };

    const xRadius1 =
      ellipsesData.measurements.minimal_area_slice.rl_diameter / 2;
    const yRadius1 =
      ellipsesData.measurements.minimal_area_slice.ap_diameter / 2;
    const xRadius2 = ellipsesData.regression_ellipse.rl_diameter / 2;
    const yRadius2 = ellipsesData.regression_ellipse.ap_diameter / 2;

    console.log("ellipse_center", ellipse_center, xRadius1, yRadius1);
    const arcShape1 = new THREE.Shape();
    arcShape1.moveTo(0, 0);
    arcShape1.absellipse(0, 0, xRadius1, yRadius1, 0, Math.PI * 2, false);
    const holePath1 = new THREE.Path();
    holePath1.absellipse(
      0,
      0,
      xRadius1 - ringThickness,
      yRadius1 - ringThickness,
      0,
      Math.PI * 2,
      true,
    );
    arcShape1.holes.push(holePath1);
    const minEllipseMesh = new THREE.Mesh(
      new THREE.ExtrudeGeometry(arcShape1, extrudeSettings),
      new THREE.MeshPhongMaterial({ color: "#00ff00" }),
    );
    minEllipseMesh.rotation.set(Math.PI / 2, 0, 0);
    minEllipseMesh.position.set(
      ellipse_center.x,
      ellipse_center.y + ringThickness / 2,
      ellipse_center.z,
    );
    // group.add(minEllipseMesh);

    const arcShape2 = new THREE.Shape();
    arcShape2.moveTo(0, 0);
    arcShape2.absellipse(0, 0, xRadius2, yRadius2, 0, Math.PI * 2, false);
    const holePath2 = new THREE.Path();
    holePath2.absellipse(
      0,
      0,
      xRadius2 - ringThickness,
      yRadius2 - ringThickness,
      0,
      Math.PI * 2,
      true,
    );
    arcShape2.holes.push(holePath2);
    const regEllipseMesh = new THREE.Mesh(
      new THREE.ExtrudeGeometry(arcShape2, extrudeSettings),
      new THREE.MeshPhongMaterial({ color: "#800080" }),
    );
    regEllipseMesh.rotation.set(Math.PI / 2, 0, 0);
    regEllipseMesh.position.set(
      ellipse_center.x,
      ellipse_center.y + ringThickness / 2,
      ellipse_center.z,
    );
    group.add(regEllipseMesh);

    ellipsesMesh = group;
  }
}

function getLineMeshFromFigures(figures, slicePosition) {
  if (figures && slicePosition) {
    let airwayCenter = airwayMesh.position;
    const group = new THREE.Group();
    for (const figure of figures) {
      let points = [];
      for (const vertice of figure.vertices) {
        let point = new THREE.Vector3(vertice.x, slicePosition, vertice.y);
        points.push(point);
      }
      if (points.length > 1) {
        let curve = createTubeCurve(points);
        let geometry = curve.geometry;
        geometry.computeBoundingBox();
        let curveBox = geometry.boundingBox;
        let curveCenter = curveBox.getCenter(new THREE.Vector3());
        geometry.center();

        let dx = airwayCenter.x - curveCenter.x;
        let dy = airwayCenter.y - curveCenter.y;
        let dz = airwayCenter.z - curveCenter.z;
        geometry.translate(-dx, -dy, -dz);

        curve.position.copy(airwayCenter);
        group.add(curve);
      }
    }
    // group.tag = AirwayMode.modeTag;
    // group.position.setY(slicePosition);
    return group.children.length > 0 ? group : null;
  }
}

function createTubeCurve(points) {
  if (!points || points.length < 2) {
    return;
  } else {
    points.push(points[0]);
  }

  let radius = defLineRadius;
  // let curve = new THREE.CurvePath();
  // for (let i = 1; i < points.length; i++) {
  //   const p0 = points[i - 1];
  //   const p1 = points[i];
  //   let lcurve = new THREE.LineCurve3(p0, p1);
  //   curve.add(lcurve);
  // }
  // curve.add(new THREE.LineCurve3(points[points.length - 1], points[0]));
  // let curve = new THREE.QuadraticBezierCurve3(points);
  // let tubeGeometry = new THREE.TubeGeometry(
  //   curve,
  //   10, // tubularSegments
  //   radius, // radius
  //   8, // radialSegments
  //   false // closed
  // );

  let tubeGeometry = DynamicTubeGeometry(
    radius,
    8,
    points.length * 2,
    false,
    false,
  );
  tubeGeometry.update(radius, points);

  let line = new THREE.Mesh(tubeGeometry, lineMeshMaterial);
  line.name = "airway_line";
  // line.tag = AirwayMode.modeTag;
  return line;
}

//#endregion

//#region UI

function selectSlice(slice) {
  // console.log("selectSlice", slice);
  airwaysData.slices.forEach((item) => {
    item.selected = item === slice;
  });
  airwaysData.has_selected = true;
  airwaysData.selected_slice = slice;

  if (sliceMesh) viewer.scene.remove(sliceMesh);
  sliceMesh = null;
  if (globalLineMesh) viewer.scene.remove(globalLineMesh);
  globalLineMesh = null;

  updateSquareOutline();
  updateLineView();
  if (_dicomLoaded) focusDicomToSlicePosition();

  updateCutSlice();
}

function updateSquareOutline() {
  if (sliceMesh) viewer.scene.remove(sliceMesh);
  if (airwaysData.isSquareOutline) {
    if (sliceMesh) viewer.scene.add(sliceMesh);
    else if (airwaysData.selected_slice) {
      let slice = airwaysData.selected_slice;
      let airwayBox = airway.defaultBox;
      let dif = Math.max(airwayBox.size.x, airwayBox.size.z) / 2;
      let centerx = airwayBox.center.x;
      let centerz = airwayBox.center.z;

      let slicePosition =
        slice.slice_position > 0
          ? airwayBox.max.y - slice.slice_position
          : airwayBox.min.y - slice.slice_position;

      let slicePoints = [
        new THREE.Vector3(centerx - dif, 0, centerz - dif),
        new THREE.Vector3(centerx + dif, 0, centerz - dif),
        new THREE.Vector3(centerx + dif, 0, centerz + dif),
        new THREE.Vector3(centerx - dif, 0, centerz + dif),
      ];

      sliceMesh = drawTubeRectangle(slicePoints);
      sliceMesh.name = "airway_rect";
      sliceMesh.tag = AirwayMode.modeTag;
      sliceMesh.position.copy(new THREE.Vector3(0, slicePosition, 0));
    }
  }
}

function updateLineView() {
  if (globalLineMesh) viewer.scene.remove(globalLineMesh);
  if (airwaysData.isLineView) {
    if (globalLineMesh) viewer.scene.add(globalLineMesh);
    else if (airwaysData.selected_slice) {
      let slice = airwaysData.selected_slice;
      globalLineMesh = airwaySliceMeshes[slice.filename];
      viewer.scene.add(globalLineMesh);
    }
  }
}

function updateCutSlice() {
  viewer.renderer.clippingPlanes = empty;
  if (airwaysData.isCutSlice && typeof currentSlicePosition === "number") {
    axialPlane.set(
      new THREE.Vector3(0, -1, 0),
      currentSlicePosition + ringThickness / 2 + 0.05,
    );
    viewer.renderer.clippingPlanes = [axialPlane];
  }
}

function switchSquareOutline() {
  console.log("switchSquareOutline");
  airwaysData.isSquareOutline = !airwaysData.isSquareOutline;
  switchToggle("squareOutlineAirwayDiv", airwaysData.isSquareOutline);

  updateSquareOutline();
}

function switchLineView() {
  console.log("switchLineView");
  airwaysData.isLineView = !airwaysData.isLineView;
  switchToggle("lineViewAirwayDiv", airwaysData.isLineView);
  updateLineView();
}

function switchAverageEllipses(value) {
  console.log("switchAverageEllipses");
  airwaysData.isAverageEllipses = value;
  switchToggle("averageAirwayDiv", airwaysData.isAverageEllipses);
  if (ellipsesMesh) viewer.scene.remove(ellipsesMesh);
  if (airwaysData.isAverageEllipses) {
    // airwayMesh.material.vertexColors = false;
    airwayMesh.material.opacity = 0.5;
    airwayMesh.material.side = THREE.FrontSide;
    // airwayMesh.material.color = new THREE.Color("#FFFFFF");
    viewer.scene.add(ellipsesMesh);
  } else {
    // airwayMesh.material.vertexColors = THREE.VertexColors;
    airwayMesh.material.opacity = 1;
    airwayMesh.material.side = THREE.DoubleSide;
  }

  // updateAverageEllipses();
}

function switchCutSlice() {
  console.log("switchCutSlice");
  airwaysData.isCutSlice = !airwaysData.isCutSlice;
  switchToggle("cutSliceAirwayDiv", airwaysData.isCutSlice);
  updateCutSlice();
}

function enlargeSlice(slice) {
  if (slice) {
    let imgSrc = slice.imageUrl; //"images/set-color.gif";
    swal({
      title: slice.name,
      icon: imgSrc,
    });
    $(".swal-title").insertBefore($(".swal-icon"));

    let $closeButton = $(
      "<button type='button' class='close swal-close' aria-label='Close' title='Close'><span aria-hidden='true'>&times;</span></button>",
    );
    let $ellipseButton = $(
      "<img src='images/info_white.png' class='airway-ellipse-btn' title='Show regression ellipse' />",
    );
    $ellipseButton.isWhite = true;
    $(".swal-modal").css("width", "620px");
    $(".swal-icon img").css("width", "610px");
    $(".swal-icon img").css("margin-bottom", "4px");
    $(".swal-title:first-child").css("margin-top", "17px");
    $(".swal-footer").css("display", "none");
    $(".swal-modal").append($closeButton);
    $(".swal-close").click(function () {
      swal.close();
    });

    if (slice.ellipsesUrl) {
      $(".swal-modal").append($ellipseButton);

      $(".airway-ellipse-btn").click(function () {
        console.log("airway-ellipse-btn click");
        $ellipseButton.isWhite = !$ellipseButton.isWhite;
        $(".airway-ellipse-btn").attr(
          "src",
          $ellipseButton.isWhite
            ? "images/info_white.png"
            : "images/info_red.png",
        );

        $(".swal-icon img").attr(
          "src",
          $ellipseButton.isWhite ? slice.imageUrl : slice.ellipsesUrl,
        );
      });
    }
  }
}

function leaveAirwayMode() {
  $(".airway-mode-item").css("display", "none");
  $(".teeth-list-ctrl").css("display", "block");
  $(".cbct-zoom-container").css("display", "block");

  if (sliceMesh) viewer.scene.remove(sliceMesh);
  sliceMesh = null;
  if (globalLineMesh) viewer.scene.remove(globalLineMesh);
  globalLineMesh = null;

  switchAverageEllipses(false);

  vueApp.allItems.forEach(function (model) {
    let mesh = viewer.get_model_mesh(model.id);
    if (mesh && mesh.geometry) mesh.visible = model.isVisible;
    model.isHidden = false;
    // viewer.set_opacity(model.id, model.opacity);
  });
  if (faceMesh && faceItem) faceMesh.visible = faceItem.isVisible;

  vueApp.switchToDefaultView();

  //   let visibleBox = getFullBox(true);
  //   let vcenter = visibleBox.center;
  //   vueApp.lookAtPoint(vcenter);

  vueApp.activeMode = null;

  AirwayMode.isActive = false;
  vueApp.leaveModes(false, true);
  viewer.renderer.clippingPlanes = empty;
}

//#endregion

export {
  AirwayMode,
  enterAirwayMode,
  leaveAirwayMode,
  selectSlice,
  enlargeSlice,
  switchSquareOutline,
  switchLineView,
  switchCutSlice,
  switchAverageEllipses,
  generateAirwayVideo,
  loadAirwaysJson,
  airwaysData,
  airwaysJsonObj,
};
